import { requestFeatureFlags } from '../services/featureFlags';
var DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';
// A Map, not an object literal: a host named `constructor` or `__proto__`
// must not find an inherited property.
var ENVIRONMENT_BY_API_HOST = new Map([
    ['api.scoreboardbuzz.com', 'production'],
    ['api.sbbqa.com', 'staging']
]);
/**
 * Name the environment from the API endpoint the page was given
 * (`sbb_api_endpoint` in the app settings). Exact host match only: anything
 * else, including a missing or unparseable endpoint, is `local`.
 */
export var flagEnvironment = function (apiEndpoint) {
    var _a;
    if (!apiEndpoint) {
        return 'local';
    }
    try {
        var hostname = new URL(apiEndpoint).hostname;
        return (_a = ENVIRONMENT_BY_API_HOST.get(hostname.toLowerCase())) !== null && _a !== void 0 ? _a : 'local';
    }
    catch (_b) {
        return 'local';
    }
};
/**
 * The flag settings from a company payload, wherever PHP put them. The pages
 * PHP renders (coach, player, scoreboard) carry them under `app_settings`;
 * the data mobile and Fire TV fetch (`/admin/get_all_data`, and the sign-in
 * answer) carries them at the top level. Nested wins when both are present.
 *
 * For the FLAG REQUEST only. Do not feed the top-level key to Analytics.init:
 * posthog-js stays uninitialised on mobile and Fire TV (James, 2026-10-11:
 * flags only there; no session recording, no autocapture).
 */
export var flagSettingsFrom = function (data) {
    var _a, _b, _c;
    return ({
        posthogKey: ((_a = data === null || data === void 0 ? void 0 : data.app_settings) === null || _a === void 0 ? void 0 : _a.posthog_key) || (data === null || data === void 0 ? void 0 : data.posthog_key),
        posthogHost: ((_b = data === null || data === void 0 ? void 0 : data.app_settings) === null || _b === void 0 ? void 0 : _b.posthog_host) || (data === null || data === void 0 ? void 0 : data.posthog_host),
        apiEndpoint: ((_c = data === null || data === void 0 ? void 0 : data.app_settings) === null || _c === void 0 ? void 0 : _c.sbb_api_endpoint) || (data === null || data === void 0 ? void 0 : data.sbb_api_endpoint)
    });
};
// A user or account id is a positive whole number, however it arrives. The
// store hands a roster user's missing `account_id` over as 0 or NaN
// (Number(null), Number(undefined)); neither is an office to ask about.
var asId = function (value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    var id = Number(value);
    // NaN fails `> 0`; Infinity is the one whole 'number' left to refuse.
    return id > 0 && id !== Infinity && Math.floor(id) === id ? String(id) : '';
};
// Counts the calls in this page. An answer is applied only if no later call
// was made while it was out.
var generation = 0;
/**
 * The entry points' one call (SCO-818; replaces utils/LaunchDarkly). Asks
 * PostHog once for this user and office and hands a clean answer to the
 * store. Until then, and whenever there is no clean answer, every flag reads
 * false.
 *
 * A page can call this more than once (mobile and Fire TV run their data init
 * again after a sign-in). Each later call first empties the store, so one
 * user's flags are never read by the next, with or without an answer, and an
 * earlier call's late answer is discarded. The first call of a page leaves
 * the store untouched: it is already empty.
 *
 * There is no stream: a flag changed in PostHog reaches a page at its next
 * load, not while it is open.
 */
var initializeFeatureFlags = function (settings, user, updateCallback) {
    generation += 1;
    var mine = generation;
    if (mine > 1) {
        try {
            updateCallback({});
        }
        catch (error) {
            console.error('Feature flags could not be cleared', error);
        }
    }
    var token = settings.posthogKey || '';
    var distinctId = asId(user.id);
    var accountId = asId(user.account_id);
    if (!token || !distinctId || !accountId) {
        return;
    }
    requestFeatureFlags({
        host: settings.posthogHost || DEFAULT_POSTHOG_HOST,
        token: token,
        distinctId: distinctId,
        accountId: accountId,
        environment: flagEnvironment(settings.apiEndpoint)
    }).then(function (flags) {
        if (flags && mine === generation) {
            updateCallback(flags);
        }
    }).catch(function (error) {
        // requestFeatureFlags never rejects; this is the store's own failure.
        console.error('Feature flags could not be applied', error);
    });
};
export default initializeFeatureFlags;
